// Typed client for /agent/* — the property-agent ("pro" role) workspace.
//
// Mirrors backend/internal/proagent/store.go. Optional / nullable fields
// follow the Go convention: empty string for "string-y" fields, null for
// numeric/date fields where 0 / epoch would be ambiguous.

import { apiJson, apiPostJson, apiPatchJson, apiDelete as apiDeleteSafe, ApiError } from "./api";
import i18n from "@/i18n";

// ─── shared enums (string-typed for FE narrowing) ────────────────────────────

export type ListingStatus = "active" | "under_offer" | "sold" | "withdrawn";
export type MandateType = "exclusive" | "co_broke";
export type BidStatus = "received" | "countered" | "accepted" | "rejected" | "withdrawn";
export type ViewingOutcome = "scheduled" | "viewed" | "no_show" | "cancelled";
export type ViewingSource = "manual" | "from_chat" | "from_buyer_app";
export type ThreadStatus = "lead" | "warm" | "cold" | "converted" | "lost";
// Buyer Leads kanban column — a single, mutually-exclusive stage the
// agent owns (distinct from ThreadStatus, which is the lead's
// "temperature"). Keep in sync with validatePipelineStage in
// backend/internal/proagent/store.go.
export type PipelineStage =
  | "new_enquiry"
  | "contacted"
  | "viewing_scheduled"
  | "viewed"
  | "interested"
  | "offer"
  | "closed";

// Canonical order + display labels for the Buyer Leads kanban — the
// single source for both the board columns and the per-card / add-lead
// stage pickers.
export const PIPELINE_STAGES: { value: PipelineStage; label: string }[] = [
  { value: "new_enquiry", label: "New Enquiry" },
  { value: "contacted", label: "Contacted" },
  { value: "viewing_scheduled", label: "Viewing Scheduled" },
  { value: "viewed", label: "Viewed" },
  { value: "interested", label: "Interested" },
  { value: "offer", label: "Offer / Negotiating" },
  { value: "closed", label: "Closed / Lost" },
];

// ─── domain types ────────────────────────────────────────────────────────────

export interface Listing {
  id: string;
  agent_id: string;
  project_name: string;
  unit_label: string;
  block: string;
  street: string;
  postal: string;
  district: string;
  property_type: string;
  beds?: number | null;
  baths?: number | null;
  area_sqft?: number | null;
  tenure: string;
  top_year?: number | null;
  asking_price?: number | null;
  // The price the seller actually wants — tracked separately from the
  // public asking_price.
  seller_target_price?: number | null;
  list_status: ListingStatus;
  mandate_type: MandateType;
  listed_at?: string | null;
  expiry_date?: string | null;
  owner_name: string;
  owner_phone: string;
  owner_email: string;
  notes: string;
  cover_image_url: string;
  created_at: string;
  updated_at: string;
  archived_at?: string | null;
}

export interface ListingInput {
  project_name?: string;
  unit_label?: string;
  block?: string;
  street?: string;
  postal?: string;
  district?: string;
  property_type?: string;
  beds?: number | null;
  baths?: number | null;
  area_sqft?: number | null;
  tenure?: string;
  top_year?: number | null;
  asking_price?: number | null;
  seller_target_price?: number | null;
  list_status?: ListingStatus;
  mandate_type?: MandateType;
  listed_at?: string | null;
  expiry_date?: string | null;
  owner_name?: string;
  owner_phone?: string;
  owner_email?: string;
  notes?: string;
  cover_image_url?: string;
}
export type ListingUpdate = ListingInput;

export interface Bid {
  id: string;
  listing_id: string;
  bidder_name: string;
  bidder_phone: string;
  amount: number;
  bid_at: string;
  status: BidStatus;
  notes: string;
  // Negotiation tracking past the initial offer — mirrors the
  // buyer-side NegotiationOffer shape. null until the seller responds.
  seller_counter?: number | null;
  seller_counter_at?: string | null;
  counter_due?: string | null;
  next_step: string;
  created_at: string;
}
export interface BidInput {
  bidder_name?: string;
  bidder_phone?: string;
  amount?: number;
  bid_at?: string | null;
  status?: BidStatus;
  notes?: string;
  seller_counter?: number | null;
  seller_counter_at?: string | null;
  counter_due?: string | null;
  next_step?: string;
}
export type BidUpdate = BidInput;

export interface ExternalURL {
  id: string;
  listing_id: string;
  platform: string;
  url: string;
  views: number;
  inquiries: number;
  last_synced?: string | null;
  notes: string;
  created_at: string;
}
export interface ExternalURLInput {
  platform?: string;
  url?: string;
  views?: number;
  inquiries?: number;
  last_synced?: string | null;
  notes?: string;
}
export type ExternalURLUpdate = ExternalURLInput;

export interface Viewing {
  id: string;
  agent_id: string;
  listing_id?: string | null;
  buyer_name: string;
  buyer_phone: string;
  buyer_user_id?: string | null;
  scheduled_at: string;
  duration_min: number;
  location: string;
  outcome: ViewingOutcome;
  feedback: string;
  // Post-viewing assessment captured in the "Log feedback" drawer.
  interest: string;        // "" | "Low" | "Medium" | "Medium-High" | "High"
  themes: string[];
  verbal_offer: string;    // free-text, e.g. "$1.43M"
  source: ViewingSource;
  source_thread_id?: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
  listing_project_name?: string;
  listing_unit_label?: string;
}
export interface ViewingInput {
  listing_id?: string | null;
  buyer_name?: string;
  buyer_phone?: string;
  buyer_user_id?: string | null;
  scheduled_at: string; // required when creating
  duration_min?: number;
  location?: string;
  outcome?: ViewingOutcome;
  feedback?: string;
  interest?: string;
  themes?: string[];
  verbal_offer?: string;
  source?: ViewingSource;
  source_thread_id?: string | null;
  notes?: string;
}
export type ViewingUpdate = Partial<ViewingInput>;

export interface ChatThread {
  id: string;
  agent_id: string;
  conversation_id?: string | null;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  status: ThreadStatus;
  pipeline_stage: PipelineStage;
  linked_listing_id?: string | null;
  notes: string;
  last_message_at?: string | null;
  created_at: string;
  updated_at: string;
}
export interface ChatThreadInput {
  conversation_id?: string | null;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  status?: ThreadStatus;
  pipeline_stage?: PipelineStage;
  linked_listing_id?: string | null;
  notes?: string;
  last_message_at?: string | null;
}
export type ChatThreadUpdate = ChatThreadInput;

export interface OverviewSnapshot {
  active_listings: number;
  under_offer: number;
  viewings_this_week: number;
  new_leads_24h: number;
  open_threads: number;
  // open_offers_count: total cross-listing bids in non-terminal status
  // (received / countered). Distinct from under_offer (listings in
  // under_offer list_status) — one listing can carry several bids.
  open_offers_count: number;
  // open_offers: most-recent 10 of those bids, enriched with listing
  // context for the dashboard's "Offers awaiting response" card.
  open_offers: OpenOfferRow[] | null;
  // reports_count: generated reports across non-archived cases.
  // reports_seller_count / reports_buyer_count split the same total
  // by agent_reports.report_type (PROP-38). They sum to reports_count
  // within a snapshot. Surface design is downstream — Dashboard wiring
  // still reads reports_count for the generic "Reports" KPI; the split
  // fields are here so the next iteration can break the chip into two
  // without another backend round-trip.
  reports_count: number;
  reports_seller_count: number;
  reports_buyer_count: number;
  today_viewings: Viewing[] | null;
  // overdue_viewings: any 'scheduled' viewing whose start time is in the
  // past, regardless of date. Part of the /agent/overview payload; the
  // retired AgentOverview surfaced it as a top-of-page nudge (PROP-141)
  // and no live surface consumes it yet.
  overdue_viewings: Viewing[] | null;
  hot_listings: Listing[] | null;
  recent_leads: ChatThread[] | null;
  listing_status_break: Record<string, number>;
}

// OpenOfferRow mirrors the backend embed of Bid + listing context.
// Server returns the Bid fields flat at the top level (Go embeds JSON-
// flatten by default), so this interface just extends Bid.
export interface OpenOfferRow extends Bid {
  listing_project_name: string;
  listing_unit_label: string;
  listing_district: string;
  listing_asking_price: number;
}

// ─── HTTP helpers ────────────────────────────────────────────────────────────

// Tolerant of empty bodies — apiJson would call .json() unconditionally
// and throw "Unexpected end of JSON input" on a valid 204 No Content.
// This delegates to api.ts's apiDelete, which checks res.ok and skips
// JSON parsing when there's nothing to parse.
async function apiDelete(path: string): Promise<void> {
  await apiDeleteSafe(path);
}

// ─── listings ────────────────────────────────────────────────────────────────

export async function listListings(opts?: { status?: string; district?: string }): Promise<Listing[]> {
  const qs = new URLSearchParams();
  if (opts?.status) qs.set("status", opts.status);
  if (opts?.district) qs.set("district", opts.district);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiJson<{ listings: Listing[] }>(`/agent/listings${suffix}`);
  return r.listings ?? [];
}

export const getListing = (id: string) => apiJson<Listing>(`/agent/listings/${id}`);

export const createListing = (in_: ListingInput) =>
  apiPostJson<Listing>("/agent/listings", in_);

export const updateListing = (id: string, patch: ListingUpdate) =>
  apiPatchJson<Listing>(`/agent/listings/${id}`, patch);

export const archiveListing = (id: string) => apiDelete(`/agent/listings/${id}`);

// ─── bids (per listing) ──────────────────────────────────────────────────────

export async function listBids(listingId: string): Promise<Bid[]> {
  const r = await apiJson<{ bids: Bid[] }>(`/agent/listings/${listingId}/bids`);
  return r.bids ?? [];
}
export const addBid = (listingId: string, in_: BidInput) =>
  apiPostJson<Bid>(`/agent/listings/${listingId}/bids`, in_);
export const updateBid = (bidId: string, patch: BidUpdate) =>
  apiPatchJson<Bid>(`/agent/bids/${bidId}`, patch);
export const deleteBid = (bidId: string) => apiDelete(`/agent/bids/${bidId}`);

// ─── external URLs ───────────────────────────────────────────────────────────

export async function listExternalUrls(listingId: string): Promise<ExternalURL[]> {
  const r = await apiJson<{ urls: ExternalURL[] }>(`/agent/listings/${listingId}/urls`);
  return r.urls ?? [];
}
export const addExternalUrl = (listingId: string, in_: ExternalURLInput) =>
  apiPostJson<ExternalURL>(`/agent/listings/${listingId}/urls`, in_);
export const updateExternalUrl = (urlId: string, patch: ExternalURLUpdate) =>
  apiPatchJson<ExternalURL>(`/agent/urls/${urlId}`, patch);
export const deleteExternalUrl = (urlId: string) => apiDelete(`/agent/urls/${urlId}`);

// ─── listing assets (photos / floorplan / brochure) ─────────────────────────

export type AssetKind = "photo" | "floorplan" | "brochure" | "video" | "other";

export interface Asset {
  id: string;
  listing_id: string;
  kind: AssetKind;
  url: string;
  label: string;
  sort_order: number;
  created_at: string;
}
export interface AssetInput {
  kind?: AssetKind;
  url: string;
  label?: string;
  sort_order?: number;
}
export type AssetUpdate = Partial<AssetInput>;

export async function listAssets(listingId: string): Promise<Asset[]> {
  const r = await apiJson<{ assets: Asset[] }>(`/agent/listings/${listingId}/assets`);
  return r.assets ?? [];
}
export const addAsset = (listingId: string, in_: AssetInput) =>
  apiPostJson<Asset>(`/agent/listings/${listingId}/assets`, in_);
export const updateAsset = (assetId: string, patch: AssetUpdate) =>
  apiPatchJson<Asset>(`/agent/assets/${assetId}`, patch);
export const deleteAsset = (assetId: string) => apiDelete(`/agent/assets/${assetId}`);

// ─── viewings ────────────────────────────────────────────────────────────────

export async function listViewings(opts?: { from?: string; to?: string }): Promise<Viewing[]> {
  const qs = new URLSearchParams();
  if (opts?.from) qs.set("from", opts.from);
  if (opts?.to) qs.set("to", opts.to);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiJson<{ viewings: Viewing[] }>(`/agent/viewings${suffix}`);
  return r.viewings ?? [];
}
export async function listViewingsForListing(listingId: string): Promise<Viewing[]> {
  const r = await apiJson<{ viewings: Viewing[] }>(`/agent/listings/${listingId}/viewings`);
  return r.viewings ?? [];
}
export const createViewing = (in_: ViewingInput) =>
  apiPostJson<Viewing>("/agent/viewings", in_);
export const updateViewing = (id: string, patch: ViewingUpdate) =>
  apiPatchJson<Viewing>(`/agent/viewings/${id}`, patch);
export const deleteViewing = (id: string) => apiDelete(`/agent/viewings/${id}`);

// ─── inbox ───────────────────────────────────────────────────────────────────

// AutoSurfacedThread — a buyer's chat conversation pulled into the
// agent's inbox automatically because of an active agent_user_link.
// Backend de-duplicates by user_id (newest conversation per buyer).
export interface AutoSurfacedThread {
  conversation_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  title: string;
  updated_at: string;
  source: "auto_link";
}

// AgentUserLink — claim relationship between agent and a buyer-side
// user. status: invited / active / revoked.
export interface AgentUserLink {
  id: string;
  agent_id: string;
  user_id: string;
  status: "invited" | "active" | "revoked";
  invited_by: "buyer" | "agent";
  notes: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  agent_name?: string;
  agent_email?: string;
}

export interface InboxResponse {
  threads: ChatThread[];
  auto_surfaced: AutoSurfacedThread[];
  pending_invites: AgentUserLink[];
}

export async function listThreads(statusFilter?: string): Promise<ChatThread[]> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
  const r = await apiJson<{ threads: ChatThread[] }>(`/agent/inbox${qs}`);
  return r.threads ?? [];
}

// Returns the new combined inbox response (manual threads + auto-
// surfaced buyer conversations + pending invites). Use this in the
// Inbox page; listThreads() is kept for narrower callers.
export async function getInbox(statusFilter?: string): Promise<InboxResponse> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
  const r = await apiJson<InboxResponse>(`/agent/inbox${qs}`);
  return {
    threads: r.threads ?? [],
    auto_surfaced: r.auto_surfaced ?? [],
    pending_invites: r.pending_invites ?? [],
  };
}

// ─── agent-user links (agent side) ───────────────────────────────────────────

export async function listLinks(statusFilter?: string): Promise<AgentUserLink[]> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
  const r = await apiJson<{ links: AgentUserLink[] }>(`/agent/links${qs}`);
  return r.links ?? [];
}
export const acceptLink = (linkID: string) =>
  apiPatchJson<AgentUserLink>(`/agent/links/${linkID}`, { status: "active" });
export const revokeLink = (linkID: string) => apiDelete(`/agent/links/${linkID}`);
export const getThread = (id: string) => apiJson<ChatThread>(`/agent/inbox/${id}`);
export const createThread = (in_: ChatThreadInput) =>
  apiPostJson<ChatThread>("/agent/inbox", in_);
export const updateThread = (id: string, patch: ChatThreadUpdate) =>
  apiPatchJson<ChatThread>(`/agent/inbox/${id}`, patch);
export const deleteThread = (id: string) => apiDelete(`/agent/inbox/${id}`);

export const convertThreadToViewing = (
  threadId: string,
  body: { scheduled_at: string; listing_id?: string | null; location?: string; duration_min?: number; notes?: string },
) => apiPostJson<Viewing>(`/agent/inbox/${threadId}/convert-to-viewing`, body);

// ─── overview ────────────────────────────────────────────────────────────────

export const getOverview = () => apiJson<OverviewSnapshot>("/agent/overview");

// ─── cross-listing open offers (PROP-37) ─────────────────────────────────────
//
// /agent/offers fronts the `/pro/offers` aggregator page.
// All filter knobs are optional — the no-arg call returns every
// non-terminal bid across the agent's non-archived listings, the
// same eligibility rule as the dashboard's "Offers awaiting response"
// card, just bounded by ProagentOpenOffersListMax instead of 10.

export type OpenOffersSort = "bid_at" | "amount" | "counter_due" | "listing";
export type OpenOffersDir = "asc" | "desc";

export interface ListOpenOffersFilter {
  // Subset of {received, countered}; omitted = both.
  status?: ("received" | "countered")[];
  listing_id?: string;
  // bid_at must be ≤ now - N days (1d granularity).
  min_age_days?: number;
  min_amount?: number;
  max_amount?: number;
  sort?: OpenOffersSort;
  dir?: OpenOffersDir;
  limit?: number;
}

export interface ListOpenOffersResp {
  rows: OpenOfferRow[];
  total: number;
}

export async function listOpenOffers(f: ListOpenOffersFilter = {}): Promise<ListOpenOffersResp> {
  const q = new URLSearchParams();
  if (f.status && f.status.length > 0) q.set("status", f.status.join(","));
  if (f.listing_id) q.set("listing", f.listing_id);
  if (f.min_age_days != null && f.min_age_days > 0) q.set("min_age_days", String(f.min_age_days));
  if (f.min_amount != null && f.min_amount > 0) q.set("min_amount", String(f.min_amount));
  if (f.max_amount != null && f.max_amount > 0) q.set("max_amount", String(f.max_amount));
  if (f.sort) q.set("sort", f.sort);
  if (f.dir) q.set("dir", f.dir);
  if (f.limit != null && f.limit > 0) q.set("limit", String(f.limit));
  const qs = q.toString();
  return apiJson<ListOpenOffersResp>(`/agent/offers${qs ? `?${qs}` : ""}`);
}

// ─── report shares (PROP-14) ─────────────────────────────────────────────────
//
// Records the agent's report-send intent. We don't operate email or
// WhatsApp gateways, so the FE flow is engineering-honest:
//   1. POST /agent/cases/{id}/report-shares — persist the intent.
//   2. Open a mailto: / whatsapp:// deep-link with the share link +
//      message pre-filled so the agent's native client takes over.
//
// The persisted row drives the dashboard timeline ("you shared this
// 3 days ago") and feeds the read-tracking roll-up planned for
// PROP-17.

export type ReportShareAudience = "seller" | "buyer";
export type ReportShareChannel = "email" | "whatsapp" | "link" | "pdf";

export interface ReportShare {
  id: string;
  case_id: string;
  agent_id: string;
  audience: ReportShareAudience;
  channels: ReportShareChannel[];
  recipient: string;
  share_link: string;
  subject: string;
  message: string;
  created_at: string;
}

export interface CreateReportShareInput {
  audience: ReportShareAudience;
  channels: ReportShareChannel[];
  recipient: string;
  share_link: string;
  subject: string;
  message: string;
}

export const recordReportShare = (caseID: string, input: CreateReportShareInput) =>
  apiPostJson<ReportShare>(`/agent/cases/${encodeURIComponent(caseID)}/report-shares`, input);

export const listReportShares = (caseID: string, limit?: number) => {
  const q = limit ? `?limit=${limit}` : "";
  return apiJson<{ rows: ReportShare[] }>(`/agent/cases/${encodeURIComponent(caseID)}/report-shares${q}`);
};

// ─── cases (case-centric IA) ─────────────────────────────────────────────────

export type CaseType = "property" | "client";
export type CaseStage =
  | "New"
  | "Active"
  | "Viewing"
  | "Offer / Negotiation"
  | "Closing"
  | "Dormant"
  | "Closed Won"
  | "Closed Lost";
export type CaseUrgency = "high" | "medium" | "low";

export interface PropertyMetrics {
  enquiries: number;
  viewings: number;
  interested: number;
  offers: number;
}
export interface ClientMetrics {
  shortlisted: number;
  viewed: number;
  interested: number;
  rejected: number;
}

export interface AgentCase {
  id: string;
  agent_id: string;
  case_type: CaseType;
  listing_id?: string | null;
  client_user_id?: string | null;
  client_name: string;
  client_phone: string;
  client_email: string;
  short_name: string;
  subject: string;
  stage: CaseStage;
  urgency: CaseUrgency;
  next_action: string;
  next_action_due: string;
  ai_note: string;
  budget_sgd?: number | null;
  created_at: string;
  updated_at: string;
  archived_at?: string | null;
  property_metrics?: PropertyMetrics | null;
  client_metrics?: ClientMetrics | null;
  // Property-case denormalisation (empty for client cases).
  listing_project_name?: string;
  listing_unit_label?: string;
  listing_asking_price?: number;
  listing_seller_target_price?: number;
  listing_district?: string;
  listing_days_on_market?: number;
  // Backend-computed: case has a next_action_due deadline that
  // already passed. Drives dashboard "overdue follow-ups" surfacing.
  next_action_overdue?: boolean;
}

export interface CaseInput {
  case_type: CaseType;
  listing_id?: string | null;
  client_user_id?: string | null;
  client_name?: string;
  client_phone?: string;
  client_email?: string;
  short_name?: string;
  subject?: string;
  stage?: CaseStage;
  urgency?: CaseUrgency;
  next_action?: string;
  next_action_due?: string;
  ai_note?: string;
  budget_sgd?: number | null;
}

export interface CaseUpdate {
  stage?: CaseStage;
  urgency?: CaseUrgency;
  next_action?: string;
  next_action_due?: string;
  ai_note?: string;
  short_name?: string;
  subject?: string;
  budget_sgd?: number | null;
  client_name?: string;
  client_phone?: string;
  client_email?: string;
}

export async function listCases(opts?: {
  type?: CaseType;
  stage?: CaseStage;
  urgency?: CaseUrgency;
}): Promise<AgentCase[]> {
  const qs = new URLSearchParams();
  if (opts?.type) qs.set("type", opts.type);
  if (opts?.stage) qs.set("stage", opts.stage);
  if (opts?.urgency) qs.set("urgency", opts.urgency);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiJson<{ cases: AgentCase[] }>(`/agent/cases${suffix}`);
  return r.cases ?? [];
}

export const getCase = (id: string) => apiJson<AgentCase>(`/agent/cases/${id}`);
export const createCase = (in_: CaseInput) => apiPostJson<AgentCase>("/agent/cases", in_);
export const updateCase = (id: string, patch: CaseUpdate) =>
  apiPatchJson<AgentCase>(`/agent/cases/${id}`, patch);
export const archiveCase = (id: string) => apiDelete(`/agent/cases/${id}`);

// CaseLeadsBundle — buyer-side activity for a property case.
// Returned by GET /agent/cases/:id/leads.
export interface CaseLeadsBundle {
  case_id: string;
  threads: ChatThread[];
  viewings: Viewing[];
  bids: Bid[];
}

export const getCaseLeads = (caseId: string) =>
  apiJson<CaseLeadsBundle>(`/agent/cases/${caseId}/leads`);

// Triggers the backend to recompute the case's ai_note via LLM.
// Returns the freshly-updated AgentCase so the caller can swap.
export const refreshCaseAINote = (caseId: string) =>
  apiPostJson<AgentCase>(`/agent/cases/${caseId}/refresh-ai-note`, {});

// ─── case reports (seller / buyer report generation) ─────────────────────────

// A report is a hybrid object: the structured fields (metrics, offers,
// comps, brief) are computed by the backend; the prose fields are
// LLM-written. Mirrors backend/internal/proagent/reports.go.

export interface ReportOffer {
  bidder_name: string;
  amount: number;
  status: string;
  seller_counter?: number | null;
  next_step: string;
}

export interface CompRow {
  label: string;
  price: number;
  psf: number;
}

export interface ReportComps {
  source: string; // "ura" | "hdb" | ""
  scope: string;
  count: number;
  psf_min: number;
  psf_median: number;
  psf_max: number;
  rows: CompRow[];
}

export interface SellerReportProse {
  buyer_feedback: string;
  market_insight: string;
  recommendation: string;
  tone: string;
}

export interface SellerReport {
  case_id: string;
  report_type: "seller";
  generated_at: string;
  listing_label: string;
  district: string;
  asking_price: number;
  target_price: number;
  days_on_market: number;
  metrics: PropertyMetrics;
  offers: ReportOffer[];
  comps: ReportComps;
  prose: SellerReportProse;
  /** Language the prose was generated in ("en" / "zh"). Absent on rows
   *  generated before the backend recorded it. */
  lang?: string;
}

export interface BuyerReportProse {
  what_you_told_us: string;
  viewing_plan: string;
  next_steps: string;
  polish: string;
}

// ReportShortlistItem is a frozen snapshot of one shortlisted property
// at report-generation time — mirrors proagent.ReportShortlistItem.
export interface ReportShortlistItem {
  name: string;
  stage: string;
  price: number;
  size: number;
  psf: number;
  why: string;
  risk: string;
}

export interface BuyerReport {
  case_id: string;
  report_type: "buyer";
  generated_at: string;
  client_name: string;
  budget_sgd?: number | null;
  metrics: ClientMetrics;
  shortlist: ReportShortlistItem[];
  prose: BuyerReportProse;
  /** Language the prose was generated in ("en" / "zh"). Absent on rows
   *  generated before the backend recorded it. */
  lang?: string;
}

export type CaseReport = SellerReport | BuyerReport;

// Fetches the persisted report for a case. Returns null when none has
// been generated yet (backend answers 404).
export async function getCaseReport(caseId: string): Promise<CaseReport | null> {
  try {
    return await apiJson<CaseReport>(`/agent/cases/${caseId}/report`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

// Triggers backend (re)generation of the case report — computes the
// structured data, runs the LLM prose pass, persists, and returns the
// full report. The response shape is identical to getCaseReport.
// lang follows the agent's current UI language so the report PROSE is
// written in that language (the structured numbers/keys stay the same).
export const generateCaseReport = (caseId: string) =>
  apiPostJson<CaseReport>(
    `/agent/cases/${caseId}/report?lang=${encodeURIComponent(i18n.language || "en")}`,
    {},
  );

// ─── suggestions (dashboard "AI suggestions" panel) ──────────────────────────

// SuggestionKind / SuggestionAction — stable string keys the FE looks
// up via i18n. Backend ships the structural fact; FE renders the copy.
export type SuggestionKind =
  | "overdue"
  | "due_today"
  | "due_tomorrow"
  | "cold"
  | "brief_incomplete";

export type SuggestionAction =
  | "draft_buyer_followup"
  | "generate_seller_update"
  | "generate_buyer_report"
  | "draft_seller_message"
  | "refine_criteria";

export interface Suggestion {
  kind: SuggestionKind;
  case: AgentCase;
  action: SuggestionAction;
  context?: Record<string, unknown>;
  priority: number;
}

export async function listSuggestions(limit?: number): Promise<Suggestion[]> {
  const qs = limit && limit > 0 ? `?limit=${limit}` : "";
  const r = await apiJson<{ suggestions: Suggestion[] }>(`/agent/suggestions${qs}`);
  return r.suggestions ?? [];
}
